<template>
  <CoreButton
    :disabled="isFetchingUserConfiguration"
    :label="t('login.messages.corporate_title', { corporate_title: 'SAML' })"
    :loading="isLoginButtonLoading"
    class="tw-mx-auto tw-mt-4"
    buttonType="secondary"
    dataTest="authorization-saml-login-button"
    type="submit"
    @click="login" />
</template>

<script lang="ts">
export default {
  name: 'AuthorizationSAML',
  inheritAttrs: false,
}
</script>

<script setup lang="ts">
import CoreButton from 'frontcore/components/CoreButton.vue'
import i18n from 'frontcore/i18n'
import { AuthorizationSAMLUseCaseFactory } from 'frontcore/domain/authorization/factory'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'

type Props = {
  applicationId?: string
  isFetchingUserConfiguration: boolean
}
const props = defineProps<Props>()

type Emits = {
  (e: 'error', message: string): void,
  (e: 'loginSuccess'): void,
}
const emit = defineEmits<Emits>()

const route = useRoute()

const { t } = i18n.global

onMounted(async () => {
  if (route.query.logout === 'true') {
    logoutHandler()
  } else {
    await loginHandler()
  }
})

const logoutHandler = () => {
  if (typeof ENV_PUBLIC_PATH !== 'undefined' ) {
    window.location.href = ENV_PUBLIC_PATH
  } else {
    window.location.href = '/'
  }
}

const { login, continueLoginProcess } = AuthorizationSAMLUseCaseFactory.build()

const isLoading = ref<boolean>(false)
const loginHandler = async () => {
  const requestId = route.query.request_id ?? null
  if (requestId === null) { return }
  isLoading.value = true
  try {
    await continueLoginProcess(requestId.toString(), props.applicationId)
    emit('loginSuccess')
  } catch (error: unknown) {
    if (error instanceof Error) {
      emit('error', error.message)
    }
    isLoading.value = false
  }
}

const isLoginButtonLoading = computed<boolean>(() => {
  return isLoading.value || (isLoading.value && props.isFetchingUserConfiguration)
})

watch(
  () => props.isFetchingUserConfiguration,
  (newValue, oldValue) => {
    if (newValue || newValue === oldValue) { return }
    isLoading.value = newValue
  }
)
</script>
